import api from './api';

export type EngagementProfile = {
  user_id: number;
  points: number;
  level: number;
  xp: number;
  username?: string;
  avatar_url?: string;
  animated_avatar_url?: string;
  avatar_upload_unlocked?: boolean;
  animated_avatar_upload_unlocked?: boolean;
  achievements: number;
};

export async function getProfile(): Promise<EngagementProfile> {
  const res = await api.get('/engagement/profile');
  return res.data as EngagementProfile;
}

export async function patchProfile(body: { username?: string }): Promise<{ ok: boolean }>{
  const res = await api.patch('/engagement/profile', body);
  return res.data as { ok: boolean };
}

export type RewardItem = {
  id: number;
  name: string;
  type: 'avatar_static' | 'avatar_animated' | 'merch_coupon' | 'merch_physical' | 'merch_digital' | 'custom' | string;
  cost_points: number;
  image_url?: string;
  stock?: number;
  active?: boolean;
  metadata?: Record<string, any>;
};

export async function getRewards(): Promise<RewardItem[]> {
  const res = await api.get('/engagement/rewards');
  return res.data as RewardItem[];
}

export async function patchAvatar(body: { avatar_url?: string; animated_avatar_url?: string }): Promise<{ ok: boolean }>{
  const res = await api.patch('/engagement/avatar', body);
  return res.data as { ok: boolean };
}

export async function redeemReward(reward_id: number): Promise<{ ok: boolean; status: string }>{
  const res = await api.post('/engagement/redeem', { reward_id });
  return res.data as { ok: boolean; status: string };
}

export type AchievementsResponse = {
  achievements: Array<{
    id: number;
    code: string;
    title: string;
    description: string;
    points: number;
    xp: number;
    icon?: string;
    achieved: boolean;
    achieved_at?: string;
  }>;
  counters: {
    comments: number;
    votes: number;
    newsletter: boolean;
  };
};

export async function getAchievements(): Promise<AchievementsResponse> {
  const res = await api.get('/engagement/achievements');
  return res.data as AchievementsResponse;
}

export type LeaderboardItem = {
  rank: number;
  user_id: number;
  first_name?: string;
  last_name?: string;
  username?: string;
  role?: string;
  points: number;
  level: number;
  xp: number;
  avatar_url?: string;
  animated_avatar_url?: string;
};

export type LeaderboardResponse = { items: LeaderboardItem[] };

export async function getLeaderboard(metric: 'points'|'level'|'xp' = 'points', limit?: number): Promise<LeaderboardResponse> {
  const res = await api.get('/engagement/leaderboard', { params: { metric, limit } });
  return res.data as LeaderboardResponse;
}

export type PointsTx = {
  id: number;
  user_id: number;
  delta: number;
  xp_delta?: number;
  reason: string;
  meta?: Record<string, any>;
  created_at: string;
};

export async function getMyTransactions(params?: { limit?: number; reason?: string }): Promise<PointsTx[]> {
  const res = await api.get('/engagement/transactions', { params });
  return (res.data?.items || []) as PointsTx[];
}

export type CheckinResponse = { ok: boolean; awarded: boolean; points?: number; level?: number; xp?: number };

export async function checkin(): Promise<CheckinResponse> {
  const res = await api.post('/engagement/checkin');
  return res.data as CheckinResponse;
}

export type ArticleReadResponse = { ok: boolean; awarded: boolean; points?: number; level?: number; xp?: number };

export async function articleRead(article_id: number): Promise<ArticleReadResponse> {
  const res = await api.post('/engagement/article-read', { article_id });
  return res.data as ArticleReadResponse;
}
